import React, { useState, useEffect } from 'react';
import {
  Key, Plus, Trash2, Copy, Check, AlertCircle,
  Hash, FileText, Percent, Lock, Power, PowerOff,
  ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';
import { Button, Card, Input } from './ui';

const AdminSignupTokensPanel = ({ currentUser }) => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [expandedToken, setExpandedToken] = useState(null);

  // Form state for creating new token - matching signup_codes table
  const [formData, setFormData] = useState({
    code: '',
    program: '',
    defaultPw: '',
    bonusRate: '5' // Default 5% bonus rate
  });
  const [formErrors, setFormErrors] = useState({});

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';

  // Load existing tokens
  useEffect(() => {
    loadTokens();
  }, []);

  const loadTokens = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/admin/signup-codes`);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Server error: ${response.status}`);
      }

      if (result.success) {
        setTokens(result.data || []);
      } else {
        throw new Error(result.error || 'Failed to load signup codes');
      }
    } catch (error) {
      console.error('Error loading signup codes:', error);
      setError('Failed to load signup codes: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  const generateRandomPassword = () => {
    const chars = 'abcdefghjkmnpqrstuvwxyz23456789';
    let pw = '';
    for (let i = 0; i < 8; i++) {
      pw += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, defaultPw: pw });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.code.trim()) {
      errors.code = 'Signup code is required';
    } else if (formData.code.trim().length < 4) {
      errors.code = 'Code must be at least 4 characters';
    } else if (!/^[A-Z0-9]+$/.test(formData.code.trim())) {
      errors.code = 'Code must contain only uppercase letters and numbers';
    }

    if (!formData.program.trim()) {
      errors.program = 'Program name is required';
    }

    if (!formData.defaultPw.trim()) {
      errors.defaultPw = 'Default password is required';
    } else if (formData.defaultPw.trim().length < 6) {
      errors.defaultPw = 'Password must be at least 6 characters';
    }

    const bonusRate = parseFloat(formData.bonusRate);
    if (isNaN(bonusRate) || bonusRate < 0 || bonusRate > 100) {
      errors.bonusRate = 'Bonus rate must be between 0 and 100';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateToken = async () => {
    if (!validateForm()) return;

    try {
      setCreating(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/admin/signup-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: formData.code.trim().toUpperCase(),
          program: formData.program.trim(),
          default_pw: formData.defaultPw.trim(),
          bonus_rate: parseFloat(formData.bonusRate) / 100, // Store as decimal (0.05 for 5%)
          is_active: true
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Server error: ${response.status}`);
      }

      if (result.success) {
        // Add new token to list
        setTokens([result.data, ...tokens]);
        // Reset form
        setFormData({
          code: '',
          program: '',
          defaultPw: '',
          bonusRate: '5'
        });
        setShowCreateForm(false);
      } else {
        throw new Error(result.error || 'Failed to create signup code');
      }
    } catch (error) {
      console.error('Error creating signup code:', error);
      setError('Failed to create signup code: ' + error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/signup-codes/${token.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_active: !token.is_active
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Server error: ${response.status}`);
      }

      if (result.success) {
        setTokens(tokens.map(t =>
          t.id === token.id ? { ...t, is_active: !t.is_active } : t
        ));
      } else {
        throw new Error(result.error || 'Failed to update signup code');
      }
    } catch (error) {
      console.error('Error updating signup code:', error);
      setError('Failed to update signup code: ' + error.message);
    }
  };

  const handleDeleteToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to delete this signup code? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/signup-codes/${tokenId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Server error: ${response.status}`);
      }

      if (result.success) {
        setTokens(tokens.filter(t => t.id !== tokenId));
      } else {
        throw new Error(result.error || 'Failed to delete signup code');
      }
    } catch (error) {
      console.error('Error deleting signup code:', error);
      setError('Failed to delete signup code: ' + error.message);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedToken(text);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Format bonus rate for display (stored as decimal, display as percentage)
  const formatBonusRate = (rate) => {
    if (rate === null || rate === undefined) return '5%'; // Default
    const percentage = rate * 100;
    return `${percentage}%`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center mr-3 shadow-lg">
              <Key className="w-5 h-5 text-white" />
            </div>
            Signup Codes
          </h2>
          <p className="text-gray-600 mt-1">Create and manage signup codes for programs with custom bonus rates</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={loadTokens}
            disabled={loading}
            className="rounded-xl"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Code
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center text-red-700">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Create Token Form */}
      {showCreateForm && (
        <Card className="p-6 border-2 border-amber-200 bg-amber-50/50">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <Plus className="w-5 h-5 mr-2 text-amber-600" />
            Create New Signup Code
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Signup Code */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Signup Code *
              </label>
              <div className="flex space-x-2">
                <div className="flex-1">
                  <Input
                    icon={Hash}
                    placeholder="SCHOOL2025"
                    value={formData.code}
                    onChange={(e) => setFormData({
                      ...formData,
                      code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                    })}
                    error={formErrors.code}
                    className="font-mono"
                    maxLength={20}
                  />
                </div>
                <Button
                  variant="secondary"
                  onClick={generateRandomCode}
                  className="px-3"
                  title="Generate random code"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">Code families will enter during signup</p>
            </div>

            {/* Program Name */}
            <div>
              <Input
                label="Program Name *"
                icon={FileText}
                placeholder="Summer Savings Program"
                value={formData.program}
                onChange={(e) => setFormData({ ...formData, program: e.target.value })}
                error={formErrors.program}
              />
              <p className="text-xs text-gray-500 mt-1">Name shown to families when they sign up</p>
            </div>

            {/* Default Password */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Default Password *
              </label>
              <div className="flex space-x-2">
                <div className="flex-1">
                  <Input
                    icon={Lock}
                    placeholder="savings123"
                    value={formData.defaultPw}
                    onChange={(e) => setFormData({ ...formData, defaultPw: e.target.value })}
                    error={formErrors.defaultPw}
                  />
                </div>
                <Button
                  variant="secondary"
                  onClick={generateRandomPassword}
                  className="px-3"
                  title="Generate random password"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">Default password for families using this code</p>
            </div>

            {/* Bonus Rate */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Bonus Rate *
              </label>
              <div className="relative">
                <Input
                  icon={Percent}
                  type="number"
                  placeholder="5"
                  min="0"
                  max="100"
                  step="0.5"
                  value={formData.bonusRate}
                  onChange={(e) => setFormData({ ...formData, bonusRate: e.target.value })}
                  error={formErrors.bonusRate}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Bonus percentage applied to deposits (e.g., 5 = 5% bonus on deposits)
              </p>
            </div>
          </div>

          {/* Bonus Preview */}
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
            <h4 className="text-sm font-semibold text-green-800 mb-2">Bonus Preview</h4>
            <p className="text-sm text-green-700">
              With a <strong>{formData.bonusRate || 0}%</strong> bonus rate:
            </p>
            <ul className="text-sm text-green-600 mt-2 space-y-1">
              <li>• $10 deposit = ${(10 * (parseFloat(formData.bonusRate) || 0) / 100).toFixed(2)} bonus = $
                {(10 + 10 * (parseFloat(formData.bonusRate) || 0) / 100).toFixed(2)} total</li>
              <li>• $50 deposit = ${(50 * (parseFloat(formData.bonusRate) || 0) / 100).toFixed(2)} bonus = $
                {(50 + 50 * (parseFloat(formData.bonusRate) || 0) / 100).toFixed(2)} total</li>
              <li>• $100 deposit = ${(100 * (parseFloat(formData.bonusRate) || 0) / 100).toFixed(2)} bonus = $
                {(100 + 100 * (parseFloat(formData.bonusRate) || 0) / 100).toFixed(2)} total</li>
            </ul>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-amber-200">
            <Button
              variant="ghost"
              onClick={() => {
                setShowCreateForm(false);
                setFormErrors({});
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateToken}
              disabled={creating}
            >
              {creating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Create Signup Code
                </>
              )}
            </Button>
          </div>
        </Card>
      )}

      {/* Tokens List */}
      {loading ? (
        <Card className="p-8 text-center">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading signup codes...</p>
        </Card>
      ) : tokens.length === 0 ? (
        <Card className="p-8 text-center">
          <Key className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">No signup codes yet</p>
          <p className="text-sm text-gray-500">Create a signup code to allow families to join programs</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {tokens.map((token) => {
            const isExpanded = expandedToken === token.id;

            return (
              <Card key={token.id} className={`overflow-hidden ${!token.is_active ? 'opacity-60' : ''}`}>
                {/* Token Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50/80 transition-colors"
                  onClick={() => setExpandedToken(isExpanded ? null : token.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                        <div className="font-mono text-lg font-bold text-gray-900 bg-gray-100 px-3 py-1 rounded-lg">
                          {token.code}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(token.code);
                          }}
                          className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                          title="Copy code"
                        >
                          {copiedToken === token.code ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{token.program}</p>
                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Percent className="w-3 h-3 mr-1" />
                            {formatBonusRate(token.bonus_rate)} bonus
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        token.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {token.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50/50 p-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Program</p>
                        <p className="font-medium text-gray-900">{token.program}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Bonus Rate</p>
                        <p className="font-medium text-emerald-600 text-lg">{formatBonusRate(token.bonus_rate)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Default Password</p>
                        <div className="flex items-center space-x-2">
                          <code className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                            {token.default_pw}
                          </code>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(token.default_pw);
                            }}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                            title="Copy password"
                          >
                            {copiedToken === token.default_pw ? (
                              <Check className="w-3 h-3 text-green-500" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-400" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Bonus Rate Explanation */}
                    <div className="mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <p className="text-xs text-emerald-700 font-medium uppercase tracking-wide mb-1">
                        Deposit Bonus Calculation
                      </p>
                      <p className="text-sm text-emerald-600">
                        Families in this program receive a <strong>{formatBonusRate(token.bonus_rate)}</strong> bonus on every deposit.
                        Example: $50 deposit = ${(50 * (token.bonus_rate || 0.05)).toFixed(2)} bonus
                      </p>
                    </div>

                    <div className="flex justify-between">
                      <Button
                        variant={token.is_active ? "secondary" : "primary"}
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleActive(token);
                        }}
                        className="rounded-xl"
                      >
                        {token.is_active ? (
                          <>
                            <PowerOff className="w-4 h-4 mr-2" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Power className="w-4 h-4 mr-2" />
                            Activate
                          </>
                        )}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteToken(token.id);
                        }}
                        className="rounded-xl"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Summary Stats */}
      {tokens.length > 0 && (
        <Card className="p-4 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-amber-600">{tokens.length}</p>
              <p className="text-xs text-gray-600">Total Codes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {tokens.filter(t => t.is_active).length}
              </p>
              <p className="text-xs text-gray-600">Active</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-500">
                {tokens.filter(t => !t.is_active).length}
              </p>
              <p className="text-xs text-gray-600">Inactive</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminSignupTokensPanel;
